'use client'

import { LineChevronDownSmall } from '@qr-front/icons/line-chevron-down-small'
import { LineChevronUpSmall } from '@qr-front/icons/line-chevron-up-small'
import { LineCloseCircle } from '@qr-front/icons/line-close-circle'
import * as Popover from '@radix-ui/react-popover'
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group'
import { type MouseEvent, useState } from 'react'
import { cn } from 'utils/cn'

import { SmartScrollContainer } from '../../components/SmartScrollContainer'

export type DropdownOption<
	T extends Record<string, unknown> = Record<string, never>,
> = {
	value: string
	label: string
} & T

type TRadioDropdown<T extends Record<string, unknown> = Record<string, never>> =
	{
		options: DropdownOption<T>[]
		selected?: string
		onChange?: (option: DropdownOption<T>) => void
		className?: string
		placeholder: string
		triggerClassName?: string
	}

export const RadioDropdown = <
	T extends Record<string, unknown> = Record<string, never>,
>({
	options,
	selected,
	onChange,
	placeholder,
	className,
	triggerClassName,
}: TRadioDropdown<T>) => {
	const [isOpen, setIsOpen] = useState(false)

	const handleOptionSelect = (value: string) => {
		const option = options.find((opt) => opt.value === value)
		if (option) {
			onChange?.(option)
			setIsOpen(false)
		}
	}

	const handleResetSelection = (e: MouseEvent) => {
		e.stopPropagation()
		e.preventDefault()
		onChange?.({ value: '', label: '' } as DropdownOption<T>)
	}

	const getSelectedLabel = () => {
		return options.find((option) => option.value === selected)?.label ?? ''
	}

	return (
		<Popover.Root open={isOpen} onOpenChange={setIsOpen}>
			<div className={cn('relative inline-block w-fit text-left', className)}>
				<Popover.Trigger asChild>
					<button
						className={cn(
							'type-body-small flex h-10 min-w-40 items-center justify-between gap-2 rounded-8 border-none bg-white px-4 text-grey ring-1 ring-inset ring-border-grey transition focus:outline-none focus:ring-2 disabled:cursor-not-allowed [&:not([disabled])]:hover:ring-2',
							triggerClassName,
						)}
					>
						<div className="grid flex-1 items-center overflow-hidden text-left">
							<div
								className={cn(
									'pointer-events-none invisible col-start-1 row-start-1 flex h-0 flex-col',
									isOpen ? 'w-fit' : 'w-0',
								)}
								aria-hidden="true"
							>
								{options.map((opt) => (
									<span
										key={opt.value}
										className="whitespace-nowrap font-semibold"
									>
										{opt.label}
									</span>
								))}
							</div>
							<div className="col-start-1 row-start-1 flex w-fit items-center whitespace-nowrap">
								{selected && (
									<LineCloseCircle
										className="hover:text-red-500 mr-1 h-4 w-4 shrink-0 cursor-pointer transition-colors"
										onClick={handleResetSelection}
									/>
								)}
								<span className="shrink-0 text-grey">{placeholder}</span>
								{selected && (
									<div>
										<span className="mx-0.5">:</span>
										<span className="font-semibold text-blue">
											{getSelectedLabel()}
										</span>
									</div>
								)}
							</div>
						</div>

						<div className="ml-2 shrink-0">
							{isOpen ? (
								<LineChevronUpSmall className="h-4 w-4" />
							) : (
								<LineChevronDownSmall className="h-4 w-4" />
							)}
						</div>
					</button>
				</Popover.Trigger>

				<Popover.Portal>
					<Popover.Content
						align="start"
						sideOffset={4}
						className="z-50 outline-none"
					>
						<div className="flex w-fit min-w-[var(--radix-popover-trigger-width)] max-w-[90vw] flex-col rounded-8 bg-white p-2 shadow-icon animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95">
							<SmartScrollContainer
								className="flex flex-col"
								direction="vertical"
								maxHeight={280}
								scrollbarPaddingHeight={0}
							>
								<RadioGroupPrimitive.Root
									value={selected}
									onValueChange={handleOptionSelect}
									className="flex flex-col gap-1"
								>
									{options.map((option) => {
										const isSelected = selected === option.value
										return (
											<RadioGroupPrimitive.Item
												key={option.value}
												value={option.value}
												className={cn(
													'flex h-fit min-h-10 cursor-pointer items-center gap-3 whitespace-nowrap rounded-8 p-2 text-left outline-none transition focus:bg-light-blue hover:bg-light-blue',
													isSelected && 'bg-light-blue',
												)}
											>
												<div
													className={cn(
														'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors',
														isSelected ? 'border-blue' : 'border-grey',
													)}
												>
													<RadioGroupPrimitive.Indicator className="h-2.5 w-2.5 rounded-full bg-blue" />
												</div>
												<span className="type-body-small text-dark-grey">
													{option.label}
												</span>
											</RadioGroupPrimitive.Item>
										)
									})}
								</RadioGroupPrimitive.Root>
							</SmartScrollContainer>
						</div>
					</Popover.Content>
				</Popover.Portal>
			</div>
		</Popover.Root>
	)
}
